import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as api from "./adminApi";
import {
  Area,
  AdminShell,
  Btn,
  Card,
  Field,
  InlineForm,
  Row,
  Sel,
  StatusBadge,
  Text,
  useAsync,
} from "./components";

export function ProgrammePage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { data: p, error, reload } = useAsync(() => api.getProgramme(id), [id]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [trackTitle, setTrackTitle] = useState("");
  const [flow, setFlow] = useState("SEQUENTIAL");
  const [threshold, setThreshold] = useState(70);
  const [deletingTrackId, setDeletingTrackId] = useState<string | null>(null);
  const [trackError, setTrackError] = useState<string | null>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverError, setCoverError] = useState<string | null>(null);

  async function uploadCover(file: File) {
    setUploadingCover(true);
    setCoverError(null);
    try {
      await api.uploadProgrammeCoverImage(id, file);
      reload();
    } catch (e) {
      setCoverError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploadingCover(false);
    }
  }

  function wrapSelection(marker: string) {
    const el = descRef.current;
    if (!el) return;
    const start = el.selectionStart ?? description.length;
    const end = el.selectionEnd ?? description.length;
    const selected = description.slice(start, end) || "text";
    const next = description.slice(0, start) + marker + selected + marker + description.slice(end);
    setDescription(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + marker.length, start + marker.length + selected.length);
    });
  }

  function toggleBulletLines() {
    const el = descRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const lineStart = description.lastIndexOf("\n", start - 1) + 1;
    const nextBreak = description.indexOf("\n", end);
    const lineEnd = nextBreak === -1 ? description.length : nextBreak;
    const block = description.slice(lineStart, lineEnd);
    const lines = block.split("\n");
    const nonEmpty = lines.filter((l) => l.trim() !== "");
    const alreadyBulleted = nonEmpty.length > 0 && nonEmpty.every((l) => l.startsWith("- "));
    const newLines = lines.map((l) =>
      l.trim() === "" ? l : alreadyBulleted ? l.replace(/^-\s?/, "") : `- ${l}`,
    );
    const next = description.slice(0, lineStart) + newLines.join("\n") + description.slice(lineEnd);
    setDescription(next);
    requestAnimationFrame(() => el.focus());
  }

  useEffect(() => {
    if (p) {
      setTitle(p.title);
      setDescription(p.description ?? "");
    }
  }, [p]);

  if (error)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Programme">
        {error}
      </AdminShell>
    );
  if (!p)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Loading…">
        {null}
      </AdminShell>
    );

  const nextOrder = p.tracks.length;

  async function removeTrack(trackId: string, title: string) {
    if (
      !window.confirm(
        `Delete track "${title}"? This also deletes its modules, learning items, and assessment. This cannot be undone.`,
      )
    )
      return;
    setDeletingTrackId(trackId);
    setTrackError(null);
    try {
      await api.deleteTrack(trackId);
      reload();
    } catch (e) {
      setTrackError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingTrackId(null);
    }
  }

  return (
    <AdminShell crumbs={[{ label: "Admin", to: "/admin" }, { label: p.title }]} title={p.title}>
      <Card>
        <div
          style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginBottom: "0.8rem" }}
        >
          <StatusBadge status={p.status} />
          <Btn
            kind="ghost"
            onClick={async () => {
              await api.setPublished("programme", p.id, p.status !== "PUBLISHED");
              reload();
            }}
          >
            {p.status === "PUBLISHED" ? "Unpublish" : "Publish"}
          </Btn>
        </div>
        <InlineForm
          submitLabel="Save"
          onSubmit={async () => {
            await api.updateProgramme(p.id, { title, description });
            reload();
          }}
        >
          <Field label="Title">
            <Text value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
          <Field label="Description (shown to learners — summarise the programme outcome)">
            <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.35rem" }}>
              <Btn kind="ghost" onClick={() => wrapSelection("**")}>
                <strong>B</strong>
              </Btn>
              <Btn kind="ghost" onClick={() => wrapSelection("*")}>
                <em>I</em>
              </Btn>
              <Btn kind="ghost" onClick={toggleBulletLines}>
                • List
              </Btn>
            </div>
            <Area
              ref={descRef}
              rows={5}
              maxLength={4000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <p style={{ color: "#888", fontSize: "0.75rem", marginTop: "0.25rem" }}>
              Shown to learners with formatting: select text and click Bold/Italic, or List to turn
              lines into bullets. A blank line starts a new paragraph.
            </p>
          </Field>
        </InlineForm>
        <Field label="Cover image (shown on the learner dashboard and track page)">
          <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
            <span style={{ color: "#666", fontSize: "0.85rem" }}>
              {p.coverImageBlobRef ? "Cover image uploaded" : "No cover image yet"}
            </span>
            <input
              ref={coverInputRef}
              type="file"
              accept=".png,.jpg,.jpeg"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadCover(file);
                e.target.value = "";
              }}
            />
            <Btn
              kind="ghost"
              disabled={uploadingCover}
              onClick={() => coverInputRef.current?.click()}
            >
              {uploadingCover
                ? "Uploading…"
                : p.coverImageBlobRef
                  ? "Replace cover image"
                  : "Upload cover image"}
            </Btn>
          </div>
          {coverError && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{coverError}</p>}
          <p style={{ color: "#888", fontSize: "0.75rem", margin: "0.4rem 0" }}>
            PNG or JPG, up to 3 MB. Suggested about 1500×500 px with the important part in the
            middle — learners see it cropped, as previewed below.
          </p>
          {p.coverImageUrl && (
            <div
              style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}
            >
              <div>
                <div style={{ color: "#666", fontSize: "0.75rem", marginBottom: "0.2rem" }}>
                  Dashboard thumbnail
                </div>
                <img
                  src={p.coverImageUrl}
                  alt="Cover image dashboard thumbnail preview"
                  style={{ width: 96, height: 64, objectFit: "cover", borderRadius: 8 }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ color: "#666", fontSize: "0.75rem", marginBottom: "0.2rem" }}>
                  Track page banner
                </div>
                <img
                  src={p.coverImageUrl}
                  alt="Cover image track page banner preview"
                  style={{
                    width: "100%",
                    maxHeight: 220,
                    objectFit: "cover",
                    borderRadius: 10,
                    display: "block",
                  }}
                />
              </div>
            </div>
          )}
        </Field>
      </Card>

      <Card>
        <strong>Tracks</strong>
        {trackError && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{trackError}</p>}
        {p.tracks.length === 0 && <p style={{ color: "#666" }}>No tracks yet.</p>}
        {p.tracks
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((t) => (
            <Row key={t.id}>
              <Link to={`/admin/tracks/${t.id}`}>
                {t.order + 1}. {t.title}
              </Link>
              <span style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <StatusBadge status={t.status} />
                <Btn
                  kind="danger"
                  onClick={() => removeTrack(t.id, t.title)}
                  disabled={deletingTrackId === t.id}
                >
                  {deletingTrackId === t.id ? "Deleting…" : "Delete"}
                </Btn>
              </span>
            </Row>
          ))}
        <div style={{ marginTop: "0.8rem" }}>
          <InlineForm
            submitLabel="Add track"
            onSubmit={async () => {
              const { id: tid } = await api.createTrack({
                programmeId: p.id,
                title: trackTitle,
                order: nextOrder,
                flow,
                passThresholdPercent: threshold,
              });
              setTrackTitle("");
              nav(`/admin/tracks/${tid}`);
            }}
          >
            <Field label="Track title">
              <Text value={trackTitle} onChange={(e) => setTrackTitle(e.target.value)} required />
            </Field>
            <Field label="Flow">
              <Sel value={flow} onChange={(e) => setFlow(e.target.value)}>
                <option value="SEQUENTIAL">Sequential</option>
                <option value="FLEXIBLE">Flexible</option>
              </Sel>
            </Field>
            <Field label="Pass threshold % (0 = no graded assessment)">
              <Text
                type="number"
                min={0}
                max={100}
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
              />
            </Field>
          </InlineForm>
        </div>
      </Card>
    </AdminShell>
  );
}
